import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled from "styled-components";

import {MAIN_APP} from '../../MidiPlay.jsx';

import Colors from '../../resource/ResourceColors.jsx';
import Tones from '../../resource/ResourceTones.jsx';
import UtilDom from '../../util/UtilDom.jsx';

const MAX_TONE = 109;
const MIN_TONE = 21;
const MEDIAN_TONE = MIN_TONE + (MAX_TONE - MIN_TONE) / 2;

const DECLINE_ANGLE = 4.7151416431953275158181103401739 * Math.PI / 180;
const COS_DECLINE = Math.cos(DECLINE_ANGLE);
const SIN_DECLINE = Math.sin(DECLINE_ANGLE);
const INCLINE_ANGLE = (Math.PI / 3) - DECLINE_ANGLE;
const COS_INCLINE = Math.cos(INCLINE_ANGLE);
const SIN_INCLINE = Math.sin(INCLINE_ANGLE);

const REFRESH_INTERVAL = 10;

const HarmonicsField = styled.div`
    position: absolute;
    overflow: auto;
    width: 100%;
    top: 0; 
    bottom: 0; 
`;

const ToneNode = styled.div`
    position: absolute;
    text-align: center;
    font-weight: bold;
    border: 0.1rem solid ${Colors.grey(30)};   
    background: ${Colors.right_diagonal_gradient(95, 80)};
    color: ${Colors.primary(20)};
    box-shadow: inset ${Colors.wide_raw_shadow(40, true)}}
    z-index:100;
`;

const ToneFrame = styled.div`
    position: absolute;
    border-radius: 0.5rem;
    border: 0.25rem solid ${Colors.grey(60)}; 
    box-shadow: inset ${Colors.wide_raw_shadow(35)}; 
    background: ${Colors.right_diagonal_gradient_grey(50, 25)};
    z-index:1;
`;

const ToneStem = styled.div`
    position: absolute;
    box-shadow: inset ${Colors.wide_raw_shadow(20, true)}}
    opacity: 0.65;
    z-index: 10;
`;

export class ViewerHarmonies extends Component {

    static propTypes = {
        events: PropTypes.object.isRequired
    }

    state = {
        center: 62,
        field_bounds: null,
        field_bounds_ref: React.createRef(),
        segment_length: -10,
        tone_cells: [],
        tone_frame_position: {}
    };

    componentDidMount() {
        const field_bounds = this.state.field_bounds_ref.current.getBoundingClientRect();
        const segment_length = field_bounds.height / 9;
        const node_width_px = field_bounds.height / 18;
        this.generate_tone_cells(this.state.center, field_bounds, segment_length, node_width_px);
        this.setState({
            field_bounds: field_bounds,
            segment_length: segment_length,
            node_width_px: node_width_px
        });
        const that = this;
        setInterval(function () {
            if (!MAIN_APP.state.playing) {
                return;
            }
            const actives = MAIN_APP.state.profile_data.filter(p_d => p_d.active).map(p_d => p_d.action.data[0]);
            that.state.tone_cells.forEach(tone_cell => {
                tone_cell.active = actives.includes(tone_cell.tone);
            });
            that.setState({tone_cells: that.state.tone_cells})
        }, REFRESH_INTERVAL);
    }

    push_tone_cell(tone_cells, position, tone, node_width_px) {
        const hsl = Colors.note_to_hsl(tone, 0.5);
        const node_style = {
            left: position.left,
            top: position.top,
            width: node_width_px + 'px',
            height: 2 * node_width_px / 3 + 'px',
            borderRadius: node_width_px / 2 + 'px',
            fontSize: node_width_px / 3.25 + 'px',
            paddingTop: node_width_px / 3.5 + 'px',
            background: 'hsl(' + hsl.h + ', 20%,' + hsl.l + '%)',
            color: tone > MEDIAN_TONE ? Colors.grey(25) : Colors.grey(75)
        };
        const stem_width_px = node_width_px * 0.28;
        const half_stem_width_px = stem_width_px / 2;
        const half_node_width_px = node_width_px / 2;
        const stem_style = {
            left: position.left + half_node_width_px - half_stem_width_px / 2,
            top: position.top + half_node_width_px,
            width: stem_width_px + 'px',
            bottom: 3 * node_width_px / 2 + 'px',
            background: 'hsl(' + hsl.h + ', 20%,' + hsl.l + '%)',
        };
        const node = {
            node_style: node_style,
            stem_style: stem_style,
            tone: tone,
        }
        const node_style_active = UtilDom.deepCopy(node_style);
        const stem_style_active = UtilDom.deepCopy(stem_style);
        node_style_active.background = 'hsl(' + hsl.h + ', 50%,' + hsl.l + '%)';
        stem_style_active.background = 'hsl(' + hsl.h + ', 50%,' + hsl.l + '%)';
        node.node_style_active = node_style_active;
        node.stem_style_active = stem_style_active;
        tone_cells.push(node);
    }

    generate_tone_row(tone_cells, seed, position, segment_length, node_width_px) {
        this.push_tone_cell(tone_cells, position, seed, node_width_px);
        let tone = seed;
        let pos_left = position.left;
        let pos_top = position.top;
        while (true) {
            tone = Tones.raise_fifth(tone);
            if (tone > MAX_TONE) {
                break;
            }
            pos_left += segment_length * COS_DECLINE;
            pos_top += segment_length * SIN_DECLINE;
            const tone_pos = {left: pos_left, top: pos_top};
            this.push_tone_cell(tone_cells, tone_pos, tone, node_width_px);
        }
        tone = seed;
        pos_left = position.left;
        pos_top = position.top;
        while (true) {
            tone = Tones.lower_fifth(tone);
            if (tone < MIN_TONE) {
                break;
            }
            pos_left -= segment_length * COS_DECLINE;
            pos_top -= segment_length * SIN_DECLINE;
            const tone_pos = {left: pos_left, top: pos_top};
            this.push_tone_cell(tone_cells, tone_pos, tone, node_width_px);
        }
    }

    generate_tone_cells(center, field_bounds, segment_length, node_width_px) {
        const height = field_bounds.height;
        const width = field_bounds.width;
        const position = {
            left: width / 2,
            top: height / 2,
        }
        const tone_cells = [];
        let seed = center;
        this.generate_tone_row(tone_cells, seed, position, segment_length, node_width_px);

        let tone = seed;
        let pos_left = position.left;
        let pos_top = position.top;
        for (let i = 0; i < 3; i++) {
            tone = Tones.raise_major_third(tone);
            pos_top -= segment_length * SIN_INCLINE;
            pos_left += segment_length * COS_INCLINE;
            this.generate_tone_row(tone_cells, tone, {left: pos_left, top: pos_top}, segment_length, node_width_px);
        }
        tone = seed;
        pos_left = position.left;
        pos_top = position.top;
        for (let i = 0; i < 3; i++) {
            tone = Tones.lower_major_third(tone);
            pos_top += segment_length * SIN_INCLINE;
            pos_left -= segment_length * COS_INCLINE;
            this.generate_tone_row(tone_cells, tone, {left: pos_left, top: pos_top}, segment_length, node_width_px);
        }

        let min_x = field_bounds.width;
        let max_x = 0;
        let min_y = field_bounds.height;
        let max_y = 0;
        tone_cells.forEach(tone_cell => {
            if (tone_cell.node_style.left < min_x) {
                min_x = tone_cell.node_style.left;
            }
            if (tone_cell.node_style.left > max_x) {
                max_x = tone_cell.node_style.left;
            }
            if (tone_cell.node_style.top < min_y) {
                min_y = tone_cell.node_style.top;
            }
            if (tone_cell.node_style.top > max_y) {
                max_y = tone_cell.node_style.top;
            }
        });
        const field_width = max_x - min_x + 32;
        const field_height = max_y - min_y;
        const horz_margin = (field_bounds.width - field_width) / 2;
        const offset_x = horz_margin - min_x;
        const top_margin = field_bounds.height / 16;
        const offset_y = top_margin - min_y;
        tone_cells.forEach(tone_cell => {
            tone_cell.node_style.left += offset_x;
            tone_cell.node_style.top += offset_y;
            tone_cell.stem_style.left += offset_x;
            tone_cell.stem_style.top += offset_y;
            tone_cell.node_style_active.left += offset_x;
            tone_cell.node_style_active.top += offset_y;
            tone_cell.stem_style_active.left += offset_x;
            tone_cell.stem_style_active.top += offset_y;
        });
        tone_cells.sort((a, b) => a.tone - b.tone);

        const tone_frame_position = {
            left: horz_margin - node_width_px / 2,
            right: horz_margin - 3 * node_width_px / 4,
            top: top_margin - node_width_px / 2,
            height: top_margin + node_width_px + segment_length + field_height,
        };
        this.setState({
            tone_cells: tone_cells,
            tone_frame_position: tone_frame_position
        })
    }

    render() {
        const tone_components = this.state.tone_cells.map(tone_cell => {
            return <ToneNode
                key={'tonenode_' + tone_cell.tone}
                style={tone_cell.active ? tone_cell.node_style_active : tone_cell.node_style}>
                {tone_cell.tone}
            </ToneNode>
        });
        const tone_stems = this.state.tone_cells.map(tone_cell => {
            return <ToneStem
                key={'tonestem_' + tone_cell.tone}
                style={tone_cell.active ? tone_cell.stem_style_active : tone_cell.stem_style}/>
        });
        return <HarmonicsField ref={this.state.field_bounds_ref}>
            <ToneFrame style={this.state.tone_frame_position}/>
            {tone_components}
            {tone_stems}
        </HarmonicsField>
    }
}

export default ViewerHarmonies;
